import React, { useEffect, useMemo, useCallback, useRef } from 'react';
import { useField, Field, useFormikContext, getIn } from 'formik';
import { at } from 'lodash';
import {
  FormControl,
  FormLabel,
  RadioGroup,
  Grid,
  FormControlLabel,
  Radio,
  FormHelperText,
  Box,
  Typography,
} from '@mui/material';
import { bloqcibeApi } from '../../../store/slices/apiSlice';
import { useParams } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { parseIntOrZero } from '../../../util/util';
import {
  calculateIsAtLeast6MonthsConservative,
  calculateIsWithin8YearsConservative,
  getDiagnosisDateInfo,
  getSubjectAnswersCached,
  isNewValidationEnabled,
} from '../utils/validationService';

const RadioGroupField = ({
  name,
  label,
  optionSize,
  options,
  verticalAlignCenter,
  requiredDepends,
  labelFontWeight,
  requiredValidationValue,
  required,
  requiredDependOnFields,
  fieldKey,
  fieldConfig,
  setFieldValue,
  // Extract DOM-incompatible props to prevent them from being spread to DOM elements
  disabledDepends,
  disabledValidationValue,
  disabledDependsOnFields,
  infoText,
  ...props
}) => {
  const { trialId, trialSiteId, siteInitStep, subjectMasterId } = useParams();
  const sponsorId = useSelector((state) => state.auth.sponsorId);
  const [field, meta, helpers] = useField(name);
  const [touched, error] = at(meta, 'touched', 'error');
  const { values, errors, validateField, setFieldError } = useFormikContext();
  const trialSite = useSelector((state) => state.trial?.trialSiteDetail);
  const [getSubjectAnswers] = bloqcibeApi.endpoints.getSubjectAnswers.useLazyQuery();

  // Ref to track if onClick handled deselection to prevent onChange from running
  const handledByClickRef = useRef(false);
  
  // Ref to track if auto-population has been done for this field
  const autoPopulatedRef = useRef(false);

  const context = useMemo(() => {
    return {
      sponsorId,
      trialId,
      siteId: trialSite?.siteTrialData?.siteId,
      subjectMasterId,
    };
  }, [sponsorId, trialId, trialSite?.siteTrialData?.siteId, subjectMasterId]);
  
  useEffect(() => {
    (async () => {
      // List of fields that should be auto-populated only once
      const autoPopulateFields = [
        'ageCriteria',
        'diagnosedWithCDin6months',
        'severityHbi',
        'hbiAssessmentConfirmation',
        'patientsWithNoCDSTScore',
        'durationDisease',
        'priorExposure',
        'eligibilityConfirmation',
      ];

      // For auto-populate fields: skip only when last calculated value (in ref) matches current field value.
      // If they differ (or ref not set), recalculate and set the calculated value to the field.
      if (autoPopulateFields.includes(fieldKey)) {
        const lastCalculatedValue = autoPopulatedRef.current;
        const currentFieldValue = field.value !== undefined && field.value !== null && field.value !== '' ? field.value : null;
        if (lastCalculatedValue != null && lastCalculatedValue !== true && currentFieldValue != null && lastCalculatedValue === currentFieldValue) {
          // console.log('Skipping auto-population for field:', fieldKey, 'because last calculated value matches current field value:', lastCalculatedValue);
          return;
        }
        // console.log('Recalculating auto-population for field:', fieldKey, 'because last calculated value does not match current field value:', lastCalculatedValue, '->', currentFieldValue);
      }

      switch (fieldKey) {
        case 'ageCriteria':
          const _answers1 = await getSubjectAnswersCached(context, fieldConfig, getSubjectAnswers, true);
          if (!_answers1) return;
          const age = parseIntOrZero(_answers1?.[fieldConfig?.dependCategoryKey]?.[fieldConfig?.dependFieldKey]);

          if (age < 18) {
            await helpers.setValue('No', false);
            await helpers.setTouched(true, false);
            await helpers.setError(undefined);
            autoPopulatedRef.current = 'No';
          } else {
            await helpers.setValue('Yes', false);
            await helpers.setTouched(true, false);
            await helpers.setError(undefined);
            autoPopulatedRef.current = 'Yes';
          }
          break;
        case 'diagnosedWithCDin6months':
          const _answers2 = await getSubjectAnswersCached(context, fieldConfig, getSubjectAnswers, true);

          const indexEventDate = _answers2?.[fieldConfig?.dependCategoryKey]?.['indexDate'];

          if (!indexEventDate) {
            return; // If index date not available, skip auto-population
          }

          const isDiagnososDateAvailable = _answers2?.[fieldConfig?.dependCategoryKey]?.['dateOfDiagnosisAvailable'];

          if (isDiagnososDateAvailable === 'Unknown') {
            await helpers.setValue('No', false);
            await helpers.setTouched(true, false);
            await helpers.setError(undefined);
            autoPopulatedRef.current = 'No';
            return;
          }

          // Get diagnosis date (from dateOfDiagnosis or yearOfDiagnosis)
          const diagnosisDateInfo = getDiagnosisDateInfo(_answers2, fieldConfig?.dependCategoryKey, indexEventDate);
          if (!diagnosisDateInfo) {
            return; // If diagnosis date not available, skip auto-population
          }

          // Use shared conservative calculation (same function as validation).
          // For exact dates: uses raw duration in years (>= 0.5, no rounding) so >= 6 months is correctly identified.
          const atLeast6Months = calculateIsAtLeast6MonthsConservative({
            diagnosisDateInfo,
            indexDate: indexEventDate,
          });

          if (atLeast6Months === null) {
            return; // If calculation not possible, skip auto-population
          }

          if (!atLeast6Months) {
            await helpers.setValue('No', false);
            await helpers.setTouched(true, false);
            await helpers.setError(undefined);
            autoPopulatedRef.current = 'No';
            return;
          } else {
            await helpers.setValue('Yes', false);
            await helpers.setTouched(true, false);
            await helpers.setError(undefined);
            autoPopulatedRef.current = 'Yes';
          }
          break;
        case 'severityHbi':
          {
            const _answers = await getSubjectAnswersCached(context, fieldConfig, getSubjectAnswers, true);
            if (!_answers) return;

            const hbiScoreValue =
              _answers?.[fieldConfig?.dependCategoryKey]?.[
                'HBI_at_baseline_individual_scores_available__Yes__HBI_at_initiation_of_VDZ_therapy__HBI_SCORE'
              ] ||
              _answers?.[fieldConfig?.dependCategoryKey]?.[
                'HBI_at_baseline_individual_scores_available__No__HBI_score_at_initiation_of_VDZ_therapy'
              ];
            const hbiScore = parseIntOrZero(hbiScoreValue);

            if (hbiScore >= 8) {
              await helpers.setValue('Yes', false);
              await helpers.setTouched(true, false);
              await helpers.setError(undefined);
              autoPopulatedRef.current = 'Yes';
            } else {
              await helpers.setValue('No', false);
              await helpers.setTouched(true, false);
              await helpers.setError(undefined);
              autoPopulatedRef.current = 'No';
            }
          }
          break;

        case 'hbiAssessmentConfirmation':
          {
            // Use cached call with empty categoryKey/fieldKey for HBI assessment
            const hbiFieldConfig = {
              ...fieldConfig,
              dependCategoryKey: '',
              dependFieldKey: '',
            };
            const hbiAnswers = await getSubjectAnswersCached(context, hbiFieldConfig, getSubjectAnswers, true);

            if (!hbiAnswers) return;

            const baselineHBI = hbiAnswers?.HBI_at_baseline_pdl?.HBI_at_baseline_available;
            const hbiScoreAtWeek26 = hbiAnswers?.HBI_at_week_26_pdl?.HBI_at_week_26_available;
            const vdzTreatmentStopped = hbiAnswers?.HBI_at_week_26_pdl?.HBI_at_week_26_available__No__HBI_at_week_26__VDZ_treatment_stopped;
            if (baselineHBI === 'Yes' && (hbiScoreAtWeek26 === 'Yes' || (vdzTreatmentStopped === 'Yes' && hbiScoreAtWeek26 === 'No'))) {
              await helpers.setValue('Yes', false);
              await helpers.setTouched(true, false);
              await helpers.setError(undefined);
              autoPopulatedRef.current = 'Yes';
            } else {
              await helpers.setValue('No', false);
              await helpers.setTouched(true, false);
              await helpers.setError(undefined);
              autoPopulatedRef.current = 'No';
            }
          }
          break;

        case 'patientsWithNoCDSTScore':
          {
            const _answers = await getSubjectAnswersCached(context, fieldConfig, getSubjectAnswers, true);
            if (!_answers) return;

            const historyOfBowelSurgery = _answers?.[fieldConfig?.dependCategoryKey]?.cdst_history_of_bowel_surgery;
            const historyOfPriorTNFAntagonistTherapy =
              _answers?.[fieldConfig?.dependCategoryKey]?.['cdst_history_of_prior_TNF-antagonist_the'];
            const historyOfPriorFistulizingDisease =
              _answers?.[fieldConfig?.dependCategoryKey]?.['cdst_history_of_prior_fistulizing_disease'];
            const cdstBaselineAlbuminAvailable = _answers?.[fieldConfig?.dependCategoryKey]?.cdst_baseline_albumin_available;
            const cdstBaselineCRPAvailable = _answers?.[fieldConfig?.dependCategoryKey]?.cdst_baseline_crp_available;
            const baselineAlbumin = _answers?.[fieldConfig?.dependCategoryKey]?.cdst_baseline_albumin;
            const baselineCRP = _answers?.[fieldConfig?.dependCategoryKey]?.cdst_baseline_crp;

            const hasUnknownCategorical =
            historyOfBowelSurgery === 'Unknown' ||
            historyOfPriorTNFAntagonistTherapy === 'Unknown' ||
            historyOfPriorFistulizingDisease === 'Unknown';
      
          const albuminMissing =
            cdstBaselineAlbuminAvailable !== 'Yes' ||
            baselineAlbumin === null ||
            baselineAlbumin === undefined ||
            baselineAlbumin === '';
      
          const crpMissing =
            cdstBaselineCRPAvailable !== 'Yes' || baselineCRP === null || baselineCRP === undefined || baselineCRP === '';
      
          const cdstNotCalculable = hasUnknownCategorical || albuminMissing || crpMissing;

            if (!cdstNotCalculable) {
              await helpers.setValue('No', false);
              await helpers.setTouched(true, false);
              await helpers.setError(undefined);
              autoPopulatedRef.current = 'No';
            } else {
              await helpers.setValue('Yes', false);
              await helpers.setTouched(true, false);
              await helpers.setError(undefined);
              autoPopulatedRef.current = 'Yes';
            }
          }
          break;

        case 'durationDisease':
          {
            const _answers = await getSubjectAnswersCached(context, fieldConfig, getSubjectAnswers, true);
            if (!_answers) return;

            const indexEventDate = _answers?.[fieldConfig?.dependCategoryKey]?.['indexDate'];
            const isDiagnosisDateAvailable = _answers?.[fieldConfig?.dependCategoryKey]?.['dateOfDiagnosisAvailable'];

            if (isDiagnosisDateAvailable === 'Unknown') {
              await helpers.setValue('No', false);
              await helpers.setTouched(true, false);
              await helpers.setError(undefined);
              autoPopulatedRef.current = 'No';
              return;
            }

            if (!indexEventDate) return;

            const diagnosisDateInfo = getDiagnosisDateInfo(_answers, fieldConfig?.dependCategoryKey, indexEventDate);
            if (!diagnosisDateInfo) return;

            // Use conservative calculation for year-only dates
            // For exact dates: uses normal ≤ 8 year calculation
            // For year-only: excludes if indexYear - diagnosisYear >= 8
            const isWithin8Years = calculateIsWithin8YearsConservative({
              diagnosisDateInfo,
              indexDate: indexEventDate,
            });
            if (isWithin8Years === null) return;

            if (isWithin8Years) {
              await helpers.setValue('Yes', false);
              await helpers.setTouched(true, false);
              await helpers.setError(undefined);
              autoPopulatedRef.current = 'Yes';
            } else {
              await helpers.setValue('No', false);
              await helpers.setTouched(true, false);
              await helpers.setError(undefined);
              autoPopulatedRef.current = 'No';
            }
          }
          break;

        case 'priorExposure':
          const _answers = await getSubjectAnswersCached(context, fieldConfig, getSubjectAnswers, true);
          if (!_answers) return;

          const cdstAdvancedTherapiesUsedBeforeIndex =
            _answers?.[fieldConfig?.dependCategoryKey]?.['cdst_advanced_therapies_used_before_index'];

          if (cdstAdvancedTherapiesUsedBeforeIndex === '2 or more') {
            await helpers.setValue('Yes', false);
            await helpers.setTouched(true, false);
            await helpers.setError(undefined);
            autoPopulatedRef.current = 'Yes';
          } else {
            await helpers.setValue('No', false);
            await helpers.setTouched(true, false);
            await helpers.setError(undefined);
            autoPopulatedRef.current = 'No';
          }
          break;

        case 'eligibilityConfirmation':
          {
            const validation = fieldConfig?.validation?.[0];
            if (!validation?.inclusionCriteria || !validation?.exclusionCriteria) return;

            // Fetch inclusion criteria using cached call
            const inclusionFieldConfig = {
              dependStepKey: validation.inclusionCriteria.dependStepKey,
              dependSectionKey: validation.inclusionCriteria.dependSectionKey,
              dependSubSectionKey: validation.inclusionCriteria.dependSubSectionKey,
              dependCategoryKey: validation.inclusionCriteria.dependCategoryKey,
              dependFieldKey: validation.inclusionCriteria.dependFieldKey,
            };
            const inclusionAnswers = await getSubjectAnswersCached(context, inclusionFieldConfig, getSubjectAnswers, true);

            // Fetch exclusion criteria using cached call
            const exclusionFieldConfig = {
              dependStepKey: validation.exclusionCriteria.dependStepKey,
              dependSectionKey: validation.exclusionCriteria.dependSectionKey,
              dependSubSectionKey: validation.exclusionCriteria.dependSubSectionKey,
              dependCategoryKey: validation.exclusionCriteria.dependCategoryKey,
              dependFieldKey: validation.exclusionCriteria.dependFieldKey,
            };
            const exclusionAnswers = await getSubjectAnswersCached(context, exclusionFieldConfig, getSubjectAnswers, true);

            const inclusionCriteria =
              inclusionAnswers?.[validation.inclusionCriteria.dependCategoryKey]?.[
                validation.inclusionCriteria.dependFieldKey
              ];
            const exclusionCriteria =
              exclusionAnswers?.[validation.exclusionCriteria.dependCategoryKey]?.[
                validation.exclusionCriteria.dependFieldKey
              ];

            const inclusionNotAvailable = inclusionCriteria === undefined || inclusionCriteria === null || inclusionCriteria === '';
            const exclusionNotAvailable = exclusionCriteria === undefined || exclusionCriteria === null || exclusionCriteria === '';
            
            // If any criteria value is not available, show error to complete the section
            if (inclusionNotAvailable || exclusionNotAvailable) {
              let errorMessage = '';
              if (inclusionNotAvailable && exclusionNotAvailable) {
                errorMessage = 'Please complete the Inclusion and Exclusion Criteria sections';
              } else if (inclusionNotAvailable) {
                errorMessage = 'Please complete the Inclusion Criteria section';
              } else {
                errorMessage = 'Please complete the Exclusion Criteria section';
              }
              await helpers.setTouched(true, false);
              await helpers.setError(errorMessage);
              setFieldError(name, errorMessage);
              return;
            }
            
            const currentEligibilityValue = getIn(values, name);
            // If inclusion = Yes and exclusion = No, patient is eligible
            const expectedValue = (inclusionCriteria === 'Yes' && exclusionCriteria === 'No') ? 'Yes' : 'No';
            
            if (currentEligibilityValue !== expectedValue) {
              await setFieldValue(name, expectedValue, false);
              await helpers.setValue(expectedValue, false);
              await helpers.setTouched(true, false);
            }
            
            const formError = getIn(errors, name);
            if (meta.error || formError) {
              helpers.setError(undefined);
              setFieldError(name, undefined);
            }
            
            autoPopulatedRef.current = expectedValue;
          }
          break;

        case 'Exclusion_Criteria_Present':
          {
            const patientsWithOstomy = getIn(values, 'exclusionCriteria_Checklist.patientsWithOstomy');
            const priorExposure = getIn(values, 'exclusionCriteria_Checklist.priorExposure');
            const patientsWithNoCDSTScore = getIn(values, 'exclusionCriteria_Checklist.patientsWithNoCDSTScore');
            const currentExclusionValue = getIn(values, name);

            // Check if all values are available
            const exclusionValuesAvailable =
              patientsWithOstomy !== undefined && patientsWithOstomy !== null && patientsWithOstomy !== '' &&
              priorExposure !== undefined && priorExposure !== null && priorExposure !== '' &&
              patientsWithNoCDSTScore !== undefined && patientsWithNoCDSTScore !== null && patientsWithNoCDSTScore !== '';

            if (exclusionValuesAvailable) {
              // Determine the expected value
              const expectedValue = (patientsWithOstomy === 'Yes' || priorExposure === 'Yes' || patientsWithNoCDSTScore === 'Yes') ? 'Yes' : 'No';
              
              // Only set value if different to avoid unnecessary updates
              if (currentExclusionValue !== expectedValue) {
                await setFieldValue(name, expectedValue, false);
                await helpers.setValue(expectedValue, false);
                await helpers.setTouched(true, false);
              }
              
              // Always clear error when value matches expected (Yup validation may have set it)
              // Use both helpers.setError and setFieldError for comprehensive clearing
              const formError = getIn(errors, name);
              if (meta.error || formError) {
                helpers.setError(undefined);
                setFieldError(name, undefined);
              }
              autoPopulatedRef.current = expectedValue;
            }
          }
          break;

        case 'Inclusion_Criteria_Met':
          {
            const ageCriteria = getIn(values, 'inclusiveCriteria_Checklist.ageCriteria');
            const diagnosedCrohnsDisease = getIn(values, 'inclusiveCriteria_Checklist.diagnosedCrohnsDisease');
            const diagnosedWithCDin6months = getIn(values, 'inclusiveCriteria_Checklist.diagnosedWithCDin6months');
            const durationDisease = getIn(values, 'inclusiveCriteria_Checklist.durationDisease');
            const severityHbi = getIn(values, 'inclusiveCriteria_Checklist.severityHbi');
            const vdzInduction = getIn(values, 'inclusiveCriteria_Checklist.vdzInduction');
            const hbiAssessmentConfirmation = getIn(values, 'inclusiveCriteria_Checklist.hbiAssessmentConfirmation');
            const currentInclusionValue = getIn(values, name);

            // Check if all values are available
            const inclusionValuesAvailable =
              ageCriteria !== undefined && ageCriteria !== null && ageCriteria !== '' &&
              diagnosedCrohnsDisease !== undefined && diagnosedCrohnsDisease !== null && diagnosedCrohnsDisease !== '' &&
              diagnosedWithCDin6months !== undefined && diagnosedWithCDin6months !== null && diagnosedWithCDin6months !== '' &&
              durationDisease !== undefined && durationDisease !== null && durationDisease !== '' &&
              severityHbi !== undefined && severityHbi !== null && severityHbi !== '' &&
              vdzInduction !== undefined && vdzInduction !== null && vdzInduction !== '' &&
              hbiAssessmentConfirmation !== undefined && hbiAssessmentConfirmation !== null && hbiAssessmentConfirmation !== '';

            if (inclusionValuesAvailable) {
              // Determine the expected value
              const allYes = 
                ageCriteria === 'Yes' &&
                diagnosedCrohnsDisease === 'Yes' &&
                diagnosedWithCDin6months === 'Yes' &&
                durationDisease === 'Yes' &&
                severityHbi === 'Yes' &&
                vdzInduction === 'Yes' &&
                hbiAssessmentConfirmation === 'Yes';
              
              const expectedValue = allYes ? 'Yes' : 'No';
              
              // Only set value if different to avoid unnecessary updates
              if (currentInclusionValue !== expectedValue) {
                await setFieldValue(name, expectedValue, false);
                await helpers.setValue(expectedValue, false);
                await helpers.setTouched(true, false);
              }
              
              // Always clear error when value matches expected (Yup validation may have set it)
              // Use both helpers.setError and setFieldError for comprehensive clearing
              const formError = getIn(errors, name);
              if (meta.error || formError) {
                helpers.setError(undefined);
                setFieldError(name, undefined);
              }
              autoPopulatedRef.current = expectedValue;
            }
          }
          break;
        default:
          // No default action required
          break;
      }
    })();
  }, [values, name, fieldKey, trialSite, context]);

  const isRequired = useMemo(() => {
    if (Array.isArray(requiredDependOnFields) && requiredDependOnFields.length > 0) {
      for (const dep of requiredDependOnFields) {
        if (dep.requiredDepends) {
          const dependsKey = dep.requiredDepends.includes('.') ? dep.requiredDepends.split('.') : dep.requiredDepends;
          const dependsValue = dep.requiredDepends.includes('.')
            ? values[dependsKey[0]]?.[dependsKey[1]]
            : values[dependsKey];

          // Check if requiredValidationValue is provided - if so, use exact match validation
          // If not provided, check if any value exists (truthy check)
          if (
            dep.requiredValidationValue !== undefined &&
            dep.requiredValidationValue !== null &&
            dep.requiredValidationValue !== ''
          ) {
            if (dependsValue === dep.requiredValidationValue) {
              return true;
            }
          } else {
            // If requiredValidationValue is not provided, check if any value exists
            if (dependsValue !== undefined && dependsValue !== null && dependsValue !== '') {
              return true;
            }
          }
        }
      }
    } else if (requiredDepends) {
      const dependsKey = requiredDepends.includes('.') ? requiredDepends.split('.') : requiredDepends;
      const dependsValue = requiredDepends?.includes('.') ? values[dependsKey[0]]?.[dependsKey[1]] : values[dependsKey];

      // Check if requiredValidationValue is provided - if so, use exact match validation
      // If not provided, check if any value exists (truthy check)
      if (requiredValidationValue !== undefined && requiredValidationValue !== null && requiredValidationValue !== '') {
        if (dependsValue === requiredValidationValue) {
          return true;
        }
      } else {
        // If requiredValidationValue is not provided, check if any value exists
        if (dependsValue !== undefined && dependsValue !== null && dependsValue !== '') {
          return true;
        }
      }
    }

    if (required) {
      return true;
    }

    return false;
  }, [values, requiredDepends, requiredValidationValue, required, requiredDependOnFields]); 

  const isDisabled = useMemo(() => {
    if (props.disabled) {
      return true;
    }

    if (Array.isArray(disabledDependsOnFields) && disabledDependsOnFields.length > 0) {
      for (const dep of disabledDependsOnFields) {
        if (dep.disabledDepends && dep.disabledValidationValue !== undefined) {
          const dependsKey = dep.disabledDepends.includes('.') ? dep.disabledDepends.split('.') : dep.disabledDepends;
          const dependsValue = dep.disabledDepends.includes('.')
            ? values[dependsKey[0]]?.[dependsKey[1]]
            : values[dependsKey];
          if (dependsValue === dep.disabledValidationValue) {
            return true;
          }
        }
      }
    } else if (disabledDepends && disabledValidationValue !== undefined) {
      const dependsKey = disabledDepends.includes('.') ? disabledDepends.split('.') : disabledDepends;
      const dependsValue = disabledDepends?.includes('.')
        ? values[dependsKey[0]]?.[dependsKey[1]]
        : values[dependsKey];
      if (dependsValue === disabledValidationValue) {
        return true;
      }
    }
    return false;
  }, [values, disabledDepends, disabledValidationValue, props.disabled, disabledDependsOnFields]);

  /**
   * Handle clicking on a radio button option
   * If the clicked option is already selected, clear the value (deselect)
   * Otherwise, let onChange handle the selection (which includes validation)
   */
  const handleClick = useCallback(
    async (optionValue, e) => {
      if (isDisabled) {
        return; // Don't allow interaction if disabled
      }

      const currentValue = field.value || '';

      // If clicking on the already selected option, clear it
      // Set flag to prevent onChange from also handling this case
      if (String(currentValue) === String(optionValue)) {
        handledByClickRef.current = true;

        // Prevent the onChange event from firing by preventing default
        if (e) {
          e.preventDefault();
        }

        await setFieldValue(field.name, '', false);
        await helpers.setError(undefined);
        await helpers.setTouched(true);
        await helpers.setValue('', true);

        // Reset the flag after a short delay to allow for event propagation
        setTimeout(() => {
          handledByClickRef.current = false;
        }, 100);
      } else {
        // For new selections, let onChange handle it (including validation)
        handledByClickRef.current = false;
      }
    },
    [field.value, field.name, setFieldValue, helpers, isDisabled]
  );

  const handleChange = useCallback(
    async (e) => {
      // If onClick already handled the deselection, skip onChange
      if (handledByClickRef.current) {
        return;
      }

      const value = e?.target?.value;
      const _isNewValidationEnabled = isNewValidationEnabled();

      if (value !== null && value !== undefined) {
        const currentValue = field.value || '';
        // Select: set the value
        helpers.setTouched(true, false);

        // await setFieldValue(field.name, value, false);
        helpers.setValue(value, true);
        await field.onChange(e);

        const asyncValidationFields = [
          'ageCriteria',
          'diagnosedWithCDin6months',
          'severityHbi',
          'hbiAssessmentConfirmation',
          'patientsWithNoCDSTScore',
          'durationDisease',
          'priorExposure',
          'eligibilityConfirmation',
          'cdst_advanced_therapies_used_before_index',
          'Inclusion_Criteria_Met',
          'Exclusion_Criteria_Present'
        ];

        if (_isNewValidationEnabled && fieldKey && asyncValidationFields.includes(fieldKey)) {
          // Clear any existing errors to allow fresh validation
          await helpers.setError(undefined);

          try {
            // Wait for Formik state to update before validating
            // Use multiple frames to ensure state is fully updated
            await new Promise((resolve) =>
              requestAnimationFrame(() => {
                requestAnimationFrame(resolve);
              })
            );

            // Trigger validation - this will run the Yup schema including required validation
            await validateField(field.name);
          } catch (error) {
            console.error('[RadioGroupField] handleChange: Validation exception', {
              fieldKey,
              fieldName: field.name,
              value,
              error,
              errorMessage: error?.message,
              errorStack: error?.stack,
            });
          }
        } else {
          // For non-async fields, clear error and let Formik handle validation naturally
          // if (fieldConfig?.validation && fieldConfig.validation[0]) {
          //   helpers.setError(undefined);
          // }
          // Trigger validation for required fields
          // if (error && (required || requiredDepends || requiredDependOnFields)) {
          //   // Wait a bit for state to update, then validate
          //   await new Promise((resolve) =>
          //     requestAnimationFrame(() => {
          //       requestAnimationFrame(resolve);
          //     })
          //   );
          //   await validateField(field.name);
          // }
        }
      }
    },
    [
      field.value,
      field.name,
      field.onChange,
      fieldKey,
      setFieldValue,
      helpers,
      fieldConfig?.validation,
      validateField,
      required,
      requiredDepends,
      requiredDependOnFields,
      error,
    ]
  );

  function _renderHelperText() {
    const [touched, error, value] = at(meta, 'touched', 'error', 'value');
    
    // For auto-populated fields, suppress "required" error if field has a value
    // This handles the race condition between setValue and Formik validation
    const autoPopulatedFields = ['Inclusion_Criteria_Met', 'Exclusion_Criteria_Present', 'eligibilityConfirmation'];
    const isAutoPopulatedField = autoPopulatedFields.includes(fieldKey);
    const hasValue = value !== undefined && value !== null && value !== '';
    const isRequiredError = error === 'This field is required';
    
    // Suppress required error for auto-populated fields that have a value
    if (isAutoPopulatedField && hasValue && isRequiredError) {
      return ' ';
    }
    
    if (touched && error) {
      return <FormHelperText error>{error}</FormHelperText>;
    } else {
      return ' ';
    }
  }

  return (
    <FormControl
      component="fieldset"
      sx={{
        display: 'flex',
        flexDirection: fieldConfig.alignOptionsHotizontally ? 'row' : 'column',
        height: verticalAlignCenter ? '100%' : 'auto',
        justifyContent: verticalAlignCenter ? 'center' : 'space-between',
        alignItems: fieldConfig.alignOptionsHotizontally ? 'center' : undefined,
      }}
    >
      {fieldConfig.alignOptionsHotizontally ? (
        <>
          {/* Label section - 70% */}
          <Box
            sx={{
              width: fieldConfig.alignOptionsHotizontally?.labelWidth ?? '70%',
              display: 'flex',
              flexDirection: 'column',
              justifyContent: 'center',
            }}
          >
            {label && (
              <FormLabel component="legend">
                <Typography
                  sx={{ color: fieldConfig?.labelColor ?? '#5b5b5b', fontWeight: labelFontWeight ?? 'medium' }}
                  variant="subtitle1"
                  gutterBottom
                >
                  {label}
                  {label && isRequired && <span style={{ color: 'red', fontSize: 18 }}>*</span>}
                </Typography>
              </FormLabel>
            )}
            <Typography sx={{ color: '#7b7b7b' }}>{infoText}</Typography>
          </Box>
          {/* Options section - 30% */}
          <Box sx={{ width: fieldConfig.alignOptionsHotizontally?.optionWidth ?? '30%' }}>
            <RadioGroup name={field.name} value={field.value || ''} row {...props}>
              {!optionSize ? (
                <Box sx={{ paddingX: 1 }}>
                  {options?.map((option, index) => {
                    const optionValue = option.value ? option.value : option;
                    return (
                      <FormControlLabel
                        key={`${option}${name}${index}`}
                        value={optionValue}
                        control={
                          <Radio
                            disabled={isDisabled}
                            onChange={handleChange}
                            onClick={(e) => {
                              e.stopPropagation();
                              handleClick(optionValue, e);
                            }}
                          />
                        }
                        label={option.label ? option.label : option}
                      />
                    );
                  })}
                </Box>
              ) : (
                <Grid container>
                  {options?.map((option, index) => (
                    <Grid
                      key={`${option}${name}${index}`}
                      item
                      sm={optionSize?.sm}
                      lg={optionSize?.lg}
                      md={optionSize?.md}
                    >
                      <FormControlLabel
                        key={`${option}${name}`}
                        value={option.value}
                        control={
                          <Radio
                            disabled={isDisabled}
                            onChange={handleChange}
                            onClick={(e) => {
                              e.stopPropagation();
                              handleClick(option.value);
                            }}
                          />
                        }
                        label={option.label}
                      />
                    </Grid>
                  ))}
                </Grid>
              )}
            </RadioGroup>
            {_renderHelperText()}
          </Box>
        </>
      ) : (
        <>
          {label && (
            <Box>
              <FormLabel component="legend">
                <Typography
                  sx={{ color: fieldConfig?.labelColor ?? '#5b5b5b', fontWeight: labelFontWeight ?? 'medium' }}
                  variant="subtitle1"
                  gutterBottom
                >
                  {label}
                  {label && isRequired && <span style={{ color: 'red', fontSize: 18 }}>*</span>}
                </Typography>
              </FormLabel>
            </Box>
          )}
          <Typography sx={{ color: '#7b7b7b' }}>{infoText}</Typography>
          <Box>
            <RadioGroup name={field.name} value={field.value || ''} row {...props}>
              {!optionSize ? (
                <Box sx={{ paddingX: 1 }}>
                  {options?.map((option, index) => {
                    const optionValue = option.value ? option.value : option;
                    return (
                      <FormControlLabel
                        key={`${option}${name}${index}`}
                        value={optionValue}
                        control={
                          <Radio
                            disabled={isDisabled}
                            onChange={handleChange}
                            onClick={(e) => {
                              e.stopPropagation();
                              handleClick(optionValue, e);
                            }}
                          />
                        }
                        label={option.label ? option.label : option}
                      />
                    );
                  })}
                </Box>
              ) : (
                <Grid container>
                  {options?.map((option, index) => (
                    <Grid
                      key={`${option}${name}${index}`}
                      item
                      sm={optionSize?.sm}
                      lg={optionSize?.lg}
                      md={optionSize?.md}
                    >
                      <FormControlLabel
                        key={`${option}${name}`}
                        value={option.value}
                        control={
                          <Radio
                            disabled={isDisabled}
                            onChange={handleChange}
                            onClick={(e) => {
                              e.stopPropagation();
                              handleClick(option.value);
                            }}
                          />
                        }
                        label={option.label}
                      />
                    </Grid>
                  ))}
                </Grid>
              )}
            </RadioGroup>
            </Box>
            {_renderHelperText()}
        </>
      )}
     
    </FormControl>
  );
};

export default React.memo(RadioGroupField);
